interface Restaurant {
  id: number;
  name: string;
  cuisine: string;
  category: string;
  rating: number;
  reviewCount: number;
  deliveryTime: string;
  deliveryFee: string;
  distance: number;
  priceRange: number;
  image: string;
  isNew: boolean;
  discount: number | null;
  specialties: string[];
}

interface RestaurantCardProps {
  restaurant: Restaurant;
}

const PRICE_LABELS: Record<number, string> = { 1: '€', 2: '€€', 3: '€€€' };

export default function RestaurantCard({ restaurant }: RestaurantCardProps) {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group border border-gray-100">
      <div className="relative h-52 w-full overflow-hidden">
        <img
          src={restaurant.image}
          alt={restaurant.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {restaurant.isNew && (
            <span className="bg-teal-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow">
              Nouveau
            </span>
          )}
          {restaurant.discount && (
            <span className="bg-orange-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow">
              -{restaurant.discount}%
            </span>
          )}
        </div>

        {/* Distance badge */}
        <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-teal-600 text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-800">{restaurant.distance} km</span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-1.5">
          <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors leading-tight">
            {restaurant.name}
          </h3>
          <div className="flex items-center gap-1 bg-teal-50 px-2 py-1 rounded-lg flex-shrink-0 ml-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-teal-600 text-xs"></i>
            </div>
            <span className="text-sm font-bold text-teal-700">{restaurant.rating}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <p className="text-xs text-gray-500">{restaurant.cuisine}</p>
          <span className="text-gray-300">·</span>
          <span className="text-xs font-semibold text-gray-500">{PRICE_LABELS[restaurant.priceRange]}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-400">({restaurant.reviewCount} avis)</span>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line text-gray-400"></i>
            </div>
            <span>{restaurant.deliveryTime}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-e-bike-2-line text-gray-400"></i>
            </div>
            <span>{restaurant.deliveryFee}</span>
          </div>
        </div>

        {restaurant.specialties.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {restaurant.specialties.slice(0, 3).map((s, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {s}
              </span>
            ))}
          </div>
        )}

        <button className="w-full bg-teal-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
          Commander
        </button>
      </div>
    </div>
  );
}
